import React, { memo, useEffect, useRef } from "react";
import SlideInLayout, {
  SlideInHeader,
  SlideInFooter,
} from "components/layouts/SlideInLayout";
import { TranslationText } from "components/TextTranslationContainer";
import { CustomButton } from "components/Button";

import PriceRangeSlider from "./PriceRangeSlider";
import DivideEqually from "./DivideEqually";
import PayForSpecificItem from "./PayForSpecificItem";
import SplitSummary from "./SplitSummary";
import { SplitBillTypeKeys } from ".";
import { useLang } from "hooks";
import useShowUserInfoModal from "features/basket/useShowUserInfoModal";



const SplitBill = ({
  SplitBillOptions,
  selectedOption,
  completeOrder,
  split,
  ordered,
  initialState,
  open,
  tempSplit,
  propsHasMap,
  setInitialState,
  selectedTip,
  rate,
  isSplitted=false,
  dineInServiceFee,
  isSplitInProgress=false,
  handleApplySplit = () => {},
  handleSelectOption = () => {},
  handleClearSplitBill = () => {},
  handleClearLocalState = () => {},
  handleClose = () => {},
}) => {

  const { isOpenUserInfoModal } = useShowUserInfoModal();
  const containerRef = useRef(null);
  const targetRef = useRef(null);
  const {isArabic} = useLang()
  const ComponentHasMap = {
    [SplitBillTypeKeys.CUSTOM_AMOUNT]: PriceRangeSlider,
    [SplitBillTypeKeys.DIVIDE_EQUALLY]: DivideEqually,
    [SplitBillTypeKeys.SELECTED_ITEMS]: PayForSpecificItem,
  };

  const SelectedComponent = ComponentHasMap[selectedOption?.type];

  const splitOption = tempSplit ?? split;

  const handleSlideInClose = () => {
    if(isOpenUserInfoModal) return;
    
    handleClearLocalState();
    handleClose();
  };

  // Scroll container so that the bottom of the target element aligns with the bottom of the container
  useEffect(() => {
    if (selectedOption && containerRef.current && targetRef.current) {
      const container = containerRef.current;
      const target = targetRef.current;

      const scrollTop =
        target.offsetTop + target.offsetHeight - container.clientHeight;

      container.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }
    setInitialState(propsHasMap[selectedOption?.type]?.value);
  }, [selectedOption]);

  return (
    <SlideInLayout
      open={open}
      handleClose={handleSlideInClose}
      container={{style: { height: "auto" } }}
      hideOutsideCloseButton={true}
      disableOutsideClick={isOpenUserInfoModal}
      shouldCloseOnEsc={isOpenUserInfoModal}
      shouldCloseOnOverlayClick={isOpenUserInfoModal}
    >
      <SlideInHeader
        title="Split Bill"
        onClose={handleSlideInClose}
        callBackContent={!(isSplitted) && 
          <button
            className={`text-16x font-semibold text-brand-base-primary h-11 ${isArabic ? 'w-fit' : 'w-11'}`}
            onClick={handleClearSplitBill}
          >
            <TranslationText text="Reset" />
          </button>
        }
      />

      <div
        className="p-6"
        style={{ height: "75vh", overflowY: "auto" }}
        ref={containerRef}
        >
          {!(isSplitted || split?.already || selectedOption?.type) && (
          <div className="flex flex-col gap-4">
            {SplitBillOptions.map((option) => (
              <CustomButton
                key={option?.type}
                onClick={() => handleSelectOption(option)}
                className={
                  selectedOption?.type !== option?.type
                    ? "bg-#F9FAFB border border-#EAECF0 text-16x font-medium text-dark-blue-900"
                    : ""
                }
              >
                <TranslationText text={option?.title} />
              </CustomButton>
            ))}
          </div>
        )}

        <div
          className={`flex flex-col gap-4 ${split?.already || isSplitted ? "" : "mt-12"}`}
          ref={targetRef}
        >
          {SelectedComponent && (
            <>
             { <TranslationText
                text={
                  split?.type === SplitBillTypeKeys.SELECTED_ITEMS
                    ? "Selected Items"
                    : split?.type === SplitBillTypeKeys.DIVIDE_EQUALLY
                    ? "Divide Equally"
                    : "Select Amount"
                }
                className="text-20x font-semibold text-dark-blue-900"
              />}
              <SelectedComponent
                completeOrder={completeOrder}
                split={split}
                ordered={ordered}
                {...propsHasMap[selectedOption?.type]}
              />
              {/* split payment history */}
              <div className="w-full border-t " />
              <SplitSummary splitOption={splitOption} selectedTip={selectedTip} taxRate={rate} dineInServiceFee={dineInServiceFee} previousTipAmount={ordered?.totalTipAmount}/>
              {Array.from({ length: 6 }).map((_, index) => (
                <br key={index} />
              ))}
              <div ref={targetRef} />
            </>
          )}
        </div>
      </div>

      <SlideInFooter>
        <CustomButton
          onClick={handleApplySplit}
          disabled={!split?.already && (
            !selectedOption?.type ||
            JSON.stringify(initialState) ===
              JSON.stringify(propsHasMap[selectedOption?.type]?.value)
          )}
        >
          <TranslationText text="Apply" />
        </CustomButton>
      </SlideInFooter>
    </SlideInLayout>
  );
};

export default memo(SplitBill);
